import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, GraduationCap, DollarSign, Calendar, Globe, Award, Book, Home } from 'lucide-react';
import { universitiesAPI, countriesAPI } from '@/lib/api';

const UniversityDetailPage = () => {
  const { id } = useParams();
  const [university, setUniversity] = useState(null);
  const [country, setCountry] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const uniRes = await universitiesAPI.getById(id);
        setUniversity(uniRes.data);
        const countryRes = await countriesAPI.getById(uniRes.data.country_id);
        setCountry(countryRes.data);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to fetch university details:', error);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="text-[#D4AF37]">در حال بارگذاری...</div></div>;
  }

  if (!university) {
    return <div className="min-h-screen flex items-center justify-center"><div className="text-zinc-400">دانشگاه یافت نشد</div></div>;
  }

  return (
    <div className="min-h-screen" data-testid="university-detail-page">
      {/* Hero */}
      <section
        className="relative h-[400px] flex items-center justify-center"
        style={{
          backgroundImage: `url(${university.image_url})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-light text-white mb-4" data-testid="university-name">{university.name_fa}</h1>
          <p className="text-xl text-zinc-300 mb-2">{university.name_en}</p>
          <div className="flex items-center justify-center gap-2 text-zinc-300">
            <MapPin size={20} />
            <span>{university.city_fa}, {country?.name_fa}</span>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            <div className="glass-effect border border-white/10 rounded-2xl p-8">
              <h2 className="text-2xl font-medium text-white mb-6">درباره دانشگاه</h2>
              <p className="text-zinc-300 leading-relaxed mb-6">{university.description}</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 py-3 px-4 bg-white/5 rounded-lg">
                  <Award className="text-[#D4AF37]" size={20} />
                  <div>
                    <div className="text-xs text-zinc-400">رتبه</div>
                    <div className="text-white font-medium">{university.rank}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 py-3 px-4 bg-white/5 rounded-lg">
                  <Book className="text-[#D4AF37]" size={20} />
                  <div>
                    <div className="text-xs text-zinc-400">نوع</div>
                    <div className="text-white font-medium">{university.university_type}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-effect border border-white/10 rounded-2xl p-8">
              <h2 className="text-2xl font-medium text-white mb-6">رشته‌های تحصیلی</h2>
              <div className="grid grid-cols-2 gap-3">
                {university.available_fields.map((field) => (
                  <div key={field} className="py-2 px-4 bg-white/5 rounded-lg text-zinc-300 text-sm">
                    {field}
                  </div>
                ))}
              </div>
            </div>

            <div className="glass-effect border border-white/10 rounded-2xl p-8">
              <h2 className="text-2xl font-medium text-white mb-6">امکانات</h2>
              <div className="grid grid-cols-2 gap-3">
                {university.facilities.map((facility) => (
                  <div key={facility} className="flex items-center gap-2 text-zinc-300 text-sm">
                    <span className="text-[#D4AF37]">•</span>
                    <span>{facility}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="glass-effect border border-white/10 rounded-2xl p-6">
              <h3 className="text-xl font-medium text-white mb-4">اطلاعات پذیرش</h3>
              <div className="space-y-4">
                <div>
                  <div className="text-sm text-zinc-400 mb-1">شهریه سالانه</div>
                  <div className="text-[#D4AF37] font-semibold">{university.tuition_range}</div>
                </div>
                <div>
                  <div className="text-sm text-zinc-400 mb-1">زبان تدریس</div>
                  <div className="text-white">{university.teaching_language}</div>
                </div>
                <div>
                  <div className="text-sm text-zinc-400 mb-1">مدرک زبان موردنیاز</div>
                  <div className="text-white">{university.language_requirement}</div>
                </div>
                <div>
                  <div className="text-sm text-zinc-400 mb-1">مهلت ثبت‌نام</div>
                  <div className="text-white">{university.application_deadline}</div>
                </div>
              </div>
            </div>

            <div className="glass-effect border border-white/10 rounded-2xl p-6">
              <h3 className="text-xl font-medium text-white mb-4">بورسیه‌ها</h3>
              <p className="text-sm text-zinc-300 leading-relaxed">{university.scholarships}</p>
            </div>

            <div className="glass-effect border border-white/10 rounded-2xl p-6">
              <h3 className="text-xl font-medium text-white mb-4">خوابگاه</h3>
              <p className="text-sm text-zinc-300 leading-relaxed">{university.dormitory}</p>
            </div>

            <Link
              to="/contact"
              data-testid="consultation-cta"
              className="block w-full px-6 py-4 bg-[#D4AF37] text-black rounded-xl text-center font-medium hover:bg-[#F3E5AB] transition-colors"
            >
              درخواست پذیرش
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UniversityDetailPage;